import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import API from '../../../services/API'
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import SaveIcon from '@mui/icons-material/Save'
import ScienceIcon from '@mui/icons-material/Science'
import TuneIcon from '@mui/icons-material/Tune'

const AddNewCell = ({ value, onChange, label }) => (
  <TextField
    size="small"
    fullWidth
    value={value}
    onChange={onChange}
    autoComplete="off"
    label={label}
  />
)

const AddNewMedicineRow = ({
  setAddMode,
  onClose,
  addNewSubmit,
  setRows,
  existingRow,
  setEditRow,
}) => {
  const hasExistingMedicine = Object.keys(existingRow)?.length > 0
  const [newDrugName, setNewDrugName] = useState(existingRow?.title ?? '')
  const [newConstitution, setNewConstitution] = useState(
    existingRow?.constitution ?? '',
  )
  const [newPresentation, setNewPresentation] = useState(
    existingRow?.presentation ?? '',
  )
  const [newLegalBasis, setNewLegalBasis] = useState(
    existingRow?.legalBasis ?? 'GSL',
  )
  const [newRoute, setNewRoute] = useState(existingRow?.route ?? 'Parenteral')
  const [newLevel, setNewLevel] = useState(
    existingRow?.level ?? ['Intermediate'],
  )
  const [newIsControlled, setNewIsControlled] = useState(
    existingRow?.isControlled ?? false,
  )
  const [newReuse, setNewReuse] = useState(existingRow?.reuse ?? false)
  const [newTrainingRequired, setNewTrainingRequired] = useState(
    existingRow?.additionalTraining ?? false,
  )
  const [newStoresAlarm, setNewStoresAlarm] = useState(
    existingRow?.minNumberInStores ?? 0,
  )
  const [newBagAlarm, setNewBagAlarm] = useState(
    existingRow?.minNumberInBags ?? 0,
  )
  const [submitError, setSubmitError] = useState(false)
  const [duplicate, setDuplicate] = useState(false)

  const [bagLevels, setBagLevels] = useState([
    'General',
    'Intermediate',
    'Advanced',
    'Specialist',
    'Other',
  ])

  useEffect(() => {
    API.get('/settings?select=medsmanagement.bagLevels').then((res) => {
      if (res.status === 200) {
        const rawBagLevels = res?.data?.medsmanagement?.bagLevels || []

        const normalisedBagLevels = rawBagLevels
          .map((level) => {
            if (typeof level === 'string') return level.trim()
            if (level?.name) return level.name.trim()
            return ''
          })
          .filter(Boolean)

        if (normalisedBagLevels.length) {
          setBagLevels(normalisedBagLevels)
        }
      }
    })
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (hasExistingMedicine) {
      API.put('/siteConfig/medicines', {
        id: existingRow._id,
        title: newDrugName,
        constitution: newConstitution,
        presentation: newPresentation,
        level: newLevel,
        legalBasis: newLegalBasis,
        additionalTraining: newTrainingRequired,
        isControlled: newIsControlled,
        route: newRoute,
        reuse: newReuse,
        minNumberInStores: newStoresAlarm,
        minNumberInBags: newBagAlarm,
      })
        .then((res) => {
          if (res.status === 200) {
            setRows(res.data)
            onClose()
          } else if (res.status === 409) {
            setDuplicate(true)
          }
        })
        .catch(() => setSubmitError(true))
    } else {
      API.post('/siteConfig/medicines', {
        title: newDrugName,
        constitution: newConstitution,
        presentation: newPresentation,
        level: newLevel,
        reuse: newReuse,
        isControlled: newIsControlled,
        additionalTraining: newTrainingRequired,
        legalBasis: newLegalBasis,
        route: newRoute,
        minNumberInStores: newStoresAlarm,
        minNumberInBags: newBagAlarm,
      })
        .then((res) => {
          if (res.status === 200) {
            setRows(res.data)
            onClose()
          } else if (res.status === 409) {
            setDuplicate(true)
          }
        })
        .catch(() => setSubmitError(true))
    }
  }

  return (
    <Stack gap={2} padding={1} component="form" onSubmit={handleSubmit}>
      <Stack direction="row" gap={1.25} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'anBlue.main',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <LocalPharmacyIcon fontSize="small" />
        </Box>
        <Box minWidth={0}>
          <Typography variant="title3">
            {hasExistingMedicine
              ? `Edit ${existingRow.title}`
              : 'Add Medicine'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure formulary details, governance flags, and stock alarms.
          </Typography>
        </Box>
      </Stack>

      {duplicate && (
        <Alert severity="error">
          A medicine with this Name, Constitution, and Presentation already
          exists.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Stack direction="row" gap={1} alignItems="center" mb={1.5}>
          <ScienceIcon color="action" fontSize="small" />
          <Typography variant="subtitle2">Medicine Details</Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.25,
          }}
        >
          <AddNewCell
            value={newDrugName}
            onChange={(e) => {
              setNewDrugName(e.target.value)
              duplicate && setDuplicate(false)
            }}
            label="Drug name"
          />
          <AddNewCell
            value={newConstitution}
            onChange={(e) => {
              setNewConstitution(e.target.value)
              duplicate && setDuplicate(false)
            }}
            label="Constitution"
          />
          <FormControl fullWidth>
            <InputLabel size="small" id="presentation-label">
              Presentation
            </InputLabel>
            <Select
              value={newPresentation}
              onChange={(e) => {
                setNewPresentation(e.target.value)
                duplicate && setDuplicate(false)
              }}
              size="small"
              labelId="presentation-label"
              label="Presentation"
            >
              <MenuItem value="Tablet">Tablet</MenuItem>
              <MenuItem value="Suspension">Suspension</MenuItem>
              <MenuItem value="Ampoule/Vial">Ampoule/Vial</MenuItem>
              <MenuItem value="Pre-Filled Syringe">Pre-Filled Syringe</MenuItem>
              <MenuItem value="Powder for Reconstitution">
                Powder for Reconstitution
              </MenuItem>
              <MenuItem value="Inhaler">Inhaler</MenuItem>
              <MenuItem value="Nebule">Nebule</MenuItem>
              <MenuItem value="Cream/Ointment">Cream/Ointment</MenuItem>
              <MenuItem value="Spray Bottle">Spray Bottle</MenuItem>
              <MenuItem value="IV Fluid Bag">IV Fluid Bag</MenuItem>
              <MenuItem value="Drops">Drops</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel size="small">Legal Basis</InputLabel>
            <Select
              value={newLegalBasis}
              onChange={(e) => setNewLegalBasis(e.target.value)}
              size="small"
              label="Legal Basis"
            >
              <MenuItem value="GSL">GSL</MenuItem>
              <MenuItem value="Schedule 19">Schedule 19 (E)</MenuItem>
              <MenuItem value="Schedule 17">Schedule 17 (P)</MenuItem>
              <MenuItem value="PGD">PGD</MenuItem>
              <MenuItem value="P">P Classified</MenuItem>
              <MenuItem value="Non-Parenteral">Non-Parenteral</MenuItem>
              <MenuItem value="POM">Prescription Only</MenuItem>
              <MenuItem value="Emergency Directive">
                Emergency Directive
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Stack direction="row" gap={1} alignItems="center" mb={1.5}>
          <TuneIcon color="action" fontSize="small" />
          <Typography variant="subtitle2">Rules and Alarms</Typography>
        </Stack>
        <Autocomplete
          value={newLevel}
          multiple
          fullWidth
          disableClearable
          options={bagLevels}
          onChange={(e, newValue) => setNewLevel(newValue)}
          renderInput={(params) => (
            <TextField {...params} size="small" fullWidth label="Bag levels" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index })
              return (
                <Chip
                  variant="outlined"
                  label={option}
                  key={key}
                  {...chipProps}
                />
              )
            })
          }
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.25,
            mt: 1.25,
          }}
        >
          <TextField
            type="number"
            size="small"
            label="Stores alarm"
            value={newStoresAlarm}
            inputProps={{ min: 0 }}
            onChange={(e) => setNewStoresAlarm(Number(e.target.value))}
          />
          <TextField
            type="number"
            size="small"
            label="Bag alarm"
            value={newBagAlarm}
            inputProps={{ min: 0 }}
            onChange={(e) => setNewBagAlarm(Number(e.target.value))}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Typography variant="subtitle2">Governance Flags</Typography>
        <Divider sx={{ my: 1 }} />
      {/* <FormControl fullWidth>
        <InputLabel>Administration Route</InputLabel>
        <Select
          value={newRoute}
          onChange={(e) => setNewRoute(e.target.value)}
          size="small"
          label="Administration Route"
        >
          <MenuItem value="Oral">Oral</MenuItem>
          <MenuItem value="Topical">Topical</MenuItem>
          <MenuItem value="Parenteral">IV/IM/IO</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
      </FormControl> */}
        <Stack gap={0.5}>
          <FormControlLabel
            label="Controlled drug"
            control={
              <Checkbox
                checked={newIsControlled}
                onChange={(e) => setNewIsControlled(e.target.checked)}
              />
            }
          />

          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newReuse}
                  onChange={(e) => setNewReuse(e.target.checked)}
                />
              }
              label="Multidose container"
            />

            <FormHelperText sx={{ mt: -0.5 }}>
              Use this for medicines supplied in a multi-dose container, where individual administrations do not reduce the recorded stock (e.g. a bottle of liquid Paracetamol).
            </FormHelperText>
          </FormControl>
          <Tooltip title="Do staff require specific training for this medicine?">
            <FormControlLabel
              label="Requires additional training"
              control={
                <Checkbox
                  checked={newTrainingRequired}
                  onChange={(e) => setNewTrainingRequired(e.target.checked)}
                />
              }
            />
          </Tooltip>
        </Stack>
      </Paper>

      <Button
        startIcon={<SaveIcon />}
        type="submit"
        variant="contained"
        fullWidth
        color="anGreen"
        disabled={duplicate}
      >
        {duplicate ? 'Resolve Duplicate' : 'Save Medicine'}
      </Button>
    </Stack>
  )
}

export default AddNewMedicineRow
